import { ChangeEvent } from "react";

import { useTranslator } from "@eo-locale/react";

import { CommonRegionQuestion } from "@/storybook-components";
import { questionNumber, questionTitleWithNumber } from "@/helpers/survey";
import {
  removeSurveyAnswers,
  setSurveyAnswers
} from "@/redux/features/survey/slice";
import { getSuburbs } from "@/models";
import type { Question, Suburb } from "@/types";
import { useAppDispatch, useAppSelector } from "@/redux/store/hooks";
import useEmbeds from "@/hooks/useEmbeds";
import googleTranslateInit from "@/helpers/google-translate-init";
import { fetchAuthToken } from "@/helpers/fetch-auth-token";
import { spacing, textStyles } from "./question-styles";

interface IRegionQuestion {
  question: Question;
}

const RegionQuestion = ({ question }: IRegionQuestion): JSX.Element => {
  const { translate } = useTranslator();
  const dispatch = useAppDispatch();
  const { isEmbed } = useEmbeds();
  const survey = useAppSelector((state) => state.survey);
  const { answers, errors, saving } = survey;
  const { baseUrl } = useAppSelector((state) => state.adapter.adapter);
  const user = useAppSelector((state) => state.user);
  const anonymousUser = useAppSelector((state) => state.anonymousUser);
  const { token } = fetchAuthToken({ user, anonymousUser });
  const { site } = useAppSelector((state) => state.site);
  const { settings } = useAppSelector((state) => state.settings);
  const { googleTranslationEnabled } = settings;

  const error = errors[question.id] || {};
  const answer = answers[question.id]?.value ?? "";
  const suburb = answer ? { label: answer, value: answer } : null;

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>): void => {
    if (!e) {
      dispatch(
        removeSurveyAnswers({
          questionId: question.id
        })
      );
      return;
    }
    dispatch(
      setSurveyAnswers({
        questionId: question.id,
        answer: { value: e.target.value }
      })
    );
  };

  const handleSelectChange = (e: { value: string }): void => {
    if (!e) {
      // need to trigger Google Translate upon clearing the field to translate the placeholder
      googleTranslateInit(isEmbed, googleTranslationEnabled);
      dispatch(
        removeSurveyAnswers({
          questionId: question.id
        })
      );
      return;
    }
    dispatch(
      setSurveyAnswers({
        questionId: question.id,
        answer: { value: e.value }
      })
    );
  };

  const fetchSuburbsData = async (query: string) => {
    return getSuburbs(token, baseUrl, {
      filterable: true,
      filters: {
        search: query
      }
    }).then((suburbs) =>
      suburbs.map((suburb: Suburb) => {
        const { name, state, zipcode, postalCode } = suburb;
        let regionValue = name;

        if (state) {
          regionValue = `${regionValue}, ${state}`;
        }

        if (postalCode) {
          regionValue = `${regionValue}, ${postalCode}`;
        }

        if (zipcode) {
          regionValue = `${regionValue} ${zipcode}`;
        }
        // need to trigger google translate when region value binds as option in the async select
        googleTranslateInit(isEmbed, googleTranslationEnabled);
        return {
          label: regionValue,
          value: regionValue
        };
      })
    );
  };
  const isAsyncSelection: boolean = site?.region !== "UNITED_KINGDOM";

  return (
    <CommonRegionQuestion
      layoutProps={{ width: "100%" }}
      isDisabled={!!saving}
      isInvalid={!!error.code}
      isRequired={question.required}
      className={error.code ? "ehq-question-error" : null}
      spacing={spacing.question}
      questionLabel={questionTitleWithNumber(
        questionNumber(question, survey),
        question
      )}
      questionLabelProps={{ ...textStyles?.label }}
      labelClassName="ehq-surveyQuestionHeading"
      accessibilityTextOfRequired={`* ${translate("common.required")}`}
      shouldFetchAsyncData={isAsyncSelection}
      answer={isAsyncSelection ? suburb : answer}
      changeEventHandler={
        isAsyncSelection ? handleSelectChange : handleInputChange
      }
      answerProps={{ ...textStyles?.content }}
      isClearable={true}
      cacheOptions={true}
      menuPlacement="auto"
      asyncHandler={fetchSuburbsData}
      placeholder={translate("survey.selectOption")}
      answerAriaLabel={translate("survey.selectOption")}
      onMenuClose={() => googleTranslateInit(isEmbed, googleTranslationEnabled)}
      onMenuOpen={() => googleTranslateInit(isEmbed, googleTranslationEnabled)}
      footerStackSpacing={spacing.footerStack}
      note={question.notes}
      noteProps={{ ...textStyles?.footer }}
      errorMessage={error?.code && translate(`errors.${error.code}`)}
      errorMessageProps={{ ...textStyles?.error }}
    />
  );
};

export default RegionQuestion;
